import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { applyThemeDefault, toggleTheme, type Theme } from '@lineageatlas/design-system';
import { CommandPalette } from '../components/CommandPalette';
import { Breadcrumbs, getConsoleRouteLabel } from './Breadcrumbs';
import { Sidebar } from './Sidebar';

function Icon({
  name,
}: {
  name: 'menu' | 'search' | 'bell' | 'help' | 'copilot' | 'moon' | 'sun';
}) {
  const paths: Record<typeof name, ReactNode> = {
    menu: <path d="M4 6h16M4 12h16M4 18h16" />,
    search: <path d="m21 21-4.3-4.3M10.8 18a7.2 7.2 0 1 1 0-14.4 7.2 7.2 0 0 1 0 14.4Z" />,
    bell: (
      <>
        <path d="M18 8.7a6 6 0 0 0-12 0v3.7L4.5 15h15L18 12.4V8.7Z" />
        <path d="M9.7 18a2.4 2.4 0 0 0 4.6 0" />
      </>
    ),
    help: (
      <>
        <circle cx="12" cy="12" r="8" />
        <path d="M9.9 9.6a2.2 2.2 0 1 1 3.8 1.5c-.8.7-1.5 1.2-1.5 2.4" />
        <path d="M12 16.4h.01" />
      </>
    ),
    copilot: (
      <>
        <path d="M12 3 14.1 8 19 10.1 14.1 12.2 12 17.2 9.9 12.2 5 10.1 9.9 8 12 3Z" />
        <path d="M18.5 15.5 19.4 18 22 18.9 19.4 19.8 18.5 22 17.6 19.8 15 18.9 17.6 18 18.5 15.5Z" />
      </>
    ),
    moon: <path d="M20.4 14.4A7 7 0 0 1 9.6 3.6a8 8 0 1 0 10.8 10.8Z" />,
    sun: (
      <>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4" />
      </>
    ),
  };

  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

interface AppShellProps {
  defaultTheme?: Theme;
}

function safeDecodeSegment(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function getConsoleTitle(pathname: string): { title: string; subtitle?: string } {
  const segments = pathname.split('/').filter(Boolean);
  const consoleSegments = segments[0] === 'console' ? segments.slice(1) : segments;
  const primary = consoleSegments[0] ?? '';
  const title = getConsoleRouteLabel(primary);

  if (consoleSegments.length > 1) {
    return { title, subtitle: safeDecodeSegment(consoleSegments[1] ?? '') };
  }

  return { title };
}

export function AppShell({ defaultTheme = 'dark' }: AppShellProps) {
  const [theme, setThemeState] = useState<Theme>(defaultTheme);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();
  const heading = useMemo(() => getConsoleTitle(location.pathname), [location.pathname]);
  const contentClassName =
    location.pathname.startsWith('/console/ai-copilot')
      ? 'rl-content rl-content--flush'
      : 'rl-content';

  useEffect(() => {
    setThemeState(applyThemeDefault(defaultTheme));
  }, [defaultTheme]);

  useEffect(() => {
    document.body.classList.toggle('rl-light', theme === 'light');
    return () => document.body.classList.remove('rl-light');
  }, [theme]);

  useEffect(() => {
    document.body.classList.toggle('nav-open', mobileNavOpen);
    return () => document.body.classList.remove('nav-open');
  }, [mobileNavOpen]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  const handleToggleTheme = useCallback(() => {
    setThemeState(toggleTheme(defaultTheme));
  }, [defaultTheme]);

  const handleCloseNav = useCallback(() => setMobileNavOpen(false), []);

  return (
    <div className={`rl${theme === 'light' ? ' rl-light' : ''}`} data-theme={theme}>
      <CommandPalette />
      <button
        className="rl-scrim"
        type="button"
        aria-label="Close navigation"
        onClick={handleCloseNav}
      />
      <Sidebar theme={theme} onThemeToggle={handleToggleTheme} onMobileClose={handleCloseNav} />

      <div className="rl-main">
        <header className="rl-top">
          <button
            className="rl-menu-btn"
            type="button"
            aria-label="Open navigation"
            title="Menu"
            onClick={() => setMobileNavOpen(true)}
          >
            <Icon name="menu" />
          </button>

          <div>
            <Breadcrumbs />
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <h1>{heading.title}</h1>
              {heading.subtitle ? <span className="sub">{heading.subtitle}</span> : null}
            </div>
          </div>

          <div className="rl-spacer" />

          <div className="rl-search" aria-label="Search patients, lots, studies">
            <Icon name="search" />
            <span>Search patients, lots, studies...</span>
            <span className="kbd">Cmd K</span>
          </div>

          <button
            className="rl-iconbtn"
            type="button"
            aria-label="Product tour"
            title="Product tour"
          >
            <Icon name="help" />
          </button>

          <button
            className="rl-iconbtn"
            type="button"
            aria-label="Notifications (3 unread)"
            title="Mentions"
          >
            <Icon name="bell" />
            <span className="badge" aria-hidden="true" />
          </button>

          <button
            className="rl-iconbtn"
            type="button"
            onClick={handleToggleTheme}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
          >
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
          </button>

          <Link className="rl-btn rl-ai-entry" to="/console/ai-copilot">
            <Icon name="copilot" />
            <span>AI Assistant</span>
          </Link>
        </header>

        <div className={contentClassName} id="rl-scroll" key={location.pathname}>
          <Outlet />
        </div>
      </div>
    </div>
  );
}
